<script>
import { GlobalPolicy } from '@/policies/global';
import LoadingView from '@/components/LoadingView.vue';
import { FormButton, SelectField, TextInput, ToggleButton, URLField, UploadFiles } from '../form';
import Promotion from '@/models/promotion';
import DateTime from '@/helpers/date';

export default {
    setup() {
        return {
            GlobalPolicy,
            Promotion
        }
    },
    data() {
        return {
            editing: false,
            saving: false
        }
    },
    methods: {
        edit() {
            this.editing = JSON.parse(JSON.stringify(this.item));
            if (!this.editing.meta) this.editing.meta = {};
            this.editing.start_at = DateTime.toDateTimeInput(this.editing._start_at);
            this.editing.end_at = DateTime.toDateTimeInput(this.editing._end_at);
        },
        cancel() {
            this.editing = false;
        },
        save() {
            if (!this.valid) return;
            this.saving = true;
            this.$emit('update', this.editing);
            this.cancel();
        }
    },
    computed: {
        small_image: {
            get() {
                if (this.editing.meta?.small_image_url) return [this.editing.meta.small_image_url];
                if (this.editing.meta?.small_image) return [this.editing.meta.small_image];
                return [];
            },
            set(value) {
                this.editing.meta.small_image_url = null;
                this.editing.meta.small_image = value[0] ?? null;
            }
        },
        large_image: {
            get() {
                if (this.editing.meta?.large_image_url) return [this.editing.meta.large_image_url];
                if (this.editing.meta?.large_image) return [this.editing.meta.large_image];
                return [];
            },
            set(value) {
                this.editing.meta.large_image_url = null;
                this.editing.meta.large_image = value[0] ?? null;
            }
        },
        asset_file: {
            get() {
                if (this.editing.file) return [this.editing.file];
                return [];
            },
            set(value) {
                this.editing.file = value[0] ?? null;
            }
        },
        valid() {
            const hasLargeImage = this.editing.meta?.large_image_url || this.editing.meta?.large_image;
            const hasSmallImage = this.editing.meta?.small_image_url || this.editing.meta?.small_image;
            return this.editing.headline?.length > 0 &&
                this.editing.button_text?.length > 0 &&
                this.editing.description?.length > 0 &&
                this.editing.type != null &&
                !!hasLargeImage &&
                this.editing.meta?.large_image_alt?.length > 0 &&
                (!hasSmallImage || this.editing.meta?.small_image_alt?.length > 0);
        }
    },
    emits: ['update'],
    props: {
        item: {
            type: Object,
            required: true
        }
    },
    components: {
        LoadingView,
        FormButton,
        TextInput,
        SelectField,
        ToggleButton,
        URLField,
        UploadFiles
    },
    created() {
        if (this.$route.path.includes('/edit') && GlobalPolicy.edit()) {
            this.edit();
        }
    }
}
</script>

<template>
    <div class="sticky top-0 flex flex-col lg:flex-row justify-between items-start">
        <h2 class="text-xl leading-9 bg-gradient-to-r from-bhav-cool-gray-900 to-bhav-cool-gray-600 text-transparent bg-clip-text font-medium font-headline antialiased">Info</h2>
        <nav v-if="GlobalPolicy.edit()" class="flex items-end gap-2">
            <FormButton @click="edit()" v-if="!editing" icon="fa-pen" icon-size="text-lg" prefix-class="fak" color="blue">Edit</FormButton>
            <template v-else>
                <FormButton @click="cancel()" color="gray">Cancel</FormButton>
                <FormButton :disabled="saving || !valid" @click="save" color="green">{{ saving ? 'Saving...' : 'Save Changes' }}</FormButton>
            </template>
        </nav>
    </div>

    <dl class="grid grid-cols-1 lg:grid-cols-5 mt-2 rounded-lg bg-white px-4" v-if="!editing">
        <dt class="font-normal text-bhav-blue-200 py-4 pr-4">Headline</dt>
        <dd class="text-bhav-blue-500 lg:col-span-4 lg:py-4 pr-4 font-normal">{{ item.headline }}</dd>

        <dt class="font-normal text-bhav-blue-200 py-4 pr-4 border-t-[2px] border-bhav-cool-gray-50">Type</dt>
        <dd class="text-bhav-blue-500 lg:col-span-4 lg:py-4 pr-4 font-normal border-t-[2px] border-bhav-cool-gray-50">{{ item.type }}</dd>

        <dt class="font-normal text-bhav-blue-200 py-4 pr-4 border-t-[2px] border-bhav-cool-gray-50">Description</dt>
        <dd class="text-bhav-blue-500 lg:col-span-4 lg:py-4 pr-4 font-normal border-t-[2px] border-bhav-cool-gray-50">{{ item.description }}</dd>

        <dt class="font-normal text-bhav-blue-200 py-4 pr-4 border-t-[2px] border-bhav-cool-gray-50">Button Text</dt>
        <dd class="text-bhav-blue-500 lg:col-span-4 lg:py-4 pr-4 font-normal border-t-[2px] border-bhav-cool-gray-50">{{ item.button_text }}</dd>

        <dt class="font-normal text-bhav-blue-200 py-4 pr-4 border-t-[2px] border-bhav-cool-gray-50">Open in New Tab</dt>
        <dd class="text-bhav-blue-500 lg:col-span-4 lg:py-4 pr-4 font-normal border-t-[2px] border-bhav-cool-gray-50">{{ item.open_in_new_tab ? 'Yes' : 'No' }}</dd>

        <dt class="font-normal text-bhav-blue-200 py-4 pr-4 border-t-[2px] border-bhav-cool-gray-50">Disclaimer</dt>
        <dd class="text-bhav-blue-500 lg:col-span-4 lg:py-4 pr-4 font-normal border-t-[2px] border-bhav-cool-gray-50">{{ item.disclaimer }}</dd>

        <dt class="font-normal text-bhav-blue-200 py-4 pr-4 border-t-[2px] border-bhav-cool-gray-50">Start Date</dt>
        <dd class="text-bhav-blue-500 lg:col-span-4 lg:py-4 pr-4 font-normal border-t-[2px] border-bhav-cool-gray-50">{{ item.start_at ? item.start : '—' }}</dd>

        <dt class="font-normal text-bhav-blue-200 py-4 pr-4 border-t-[2px] border-bhav-cool-gray-50">End Date</dt>
        <dd class="text-bhav-blue-500 lg:col-span-4 lg:py-4 pr-4 font-normal border-t-[2px] border-bhav-cool-gray-50">{{ item.end_at ? item.end : '—' }}</dd>

        <template v-if="item.url">
            <dt class="font-normal text-bhav-blue-200 py-4 pr-4 border-t-[2px] border-bhav-cool-gray-50">URL</dt>
            <dd class="lg:col-span-4 lg:py-4 pr-4 font-normal border-t-[2px] border-bhav-cool-gray-50">
                <a :href="item.url" target="_blank" rel="noopener noreferrer" class="text-bhav-blue-500 hover:underline break-all">{{ item.url }}</a>
            </dd>
        </template>

        <template v-if="item.meta?.small_image_url">
            <dt class="font-normal text-bhav-blue-200 py-4 pr-4 border-t-[2px] border-bhav-cool-gray-50">Small Image</dt>
            <dd class="lg:col-span-4 lg:py-4 pr-4 border-t-[2px] border-bhav-cool-gray-50">
                <img :src="item.meta.small_image_url" :alt="item.meta?.small_image_alt ?? item.headline" class="max-h-48 rounded-lg object-contain" />
            </dd>
        </template>
        <template v-if="item.meta?.large_image_url">
            <dt class="font-normal text-bhav-blue-200 py-4 pr-4 border-t-[2px] border-bhav-cool-gray-50">Large Image</dt>
            <dd class="lg:col-span-4 lg:py-4 pr-4 border-t-[2px] border-bhav-cool-gray-50">
                <img :src="item.meta.large_image_url" :alt="item.meta?.large_image_alt ?? item.headline" class="max-h-48 rounded-lg object-contain" />
            </dd>
        </template>

        <dt class="font-normal text-bhav-blue-200 py-4 pr-4 border-t-[2px] border-bhav-cool-gray-50">Show image inline on page</dt>
        <dd class="text-bhav-blue-500 lg:col-span-4 lg:py-4 pr-4 font-normal border-t-[2px] border-bhav-cool-gray-50">{{ item.meta?.show_image_inline ? 'Yes' : 'No' }}</dd>
    </dl>

    <div class="relative" v-else>
    <LoadingView v-if="saving" :absolute="true" />
    <form @submit.prevent="save" class="grid grid-cols-1 lg:grid-cols-5 mt-2 rounded-lg bg-white px-4">
        <legend class="sr-only">Promotion Properties</legend>

        <label for="editing.headline" class="font-normal text-bhav-blue-300 py-5">
            Headline <span class="text-oakwood-red cursor-help" title="This field is required">*</span>
        </label>
        <div class="text-oakwood-gray-900 lg:col-span-4 py-5">
            <TextInput id="editing.headline" v-model="editing.headline" placeholder="Promotion headline" required maxlength="255" class="max-w-80" />
        </div>

        <label for="editing.type" class="font-normal text-bhav-blue-300 py-5 border-t-[2px] border-bhav-cool-gray-50">
            Type <span class="text-oakwood-red cursor-help" title="This field is required">*</span>
        </label>
        <div class="text-oakwood-gray-900 lg:col-span-4 py-5 border-t-[2px] border-bhav-cool-gray-50">
            <SelectField id="editing.type" v-model="editing.type" :options="Promotion.types" :allow-null="false" class="max-w-80" />
        </div>

        <label for="editing.description" class="font-normal text-bhav-blue-300 py-5 border-t-[2px] border-bhav-cool-gray-50">
            Description <span class="text-oakwood-red cursor-help" title="This field is required">*</span>
        </label>
        <div class="text-oakwood-gray-900 lg:col-span-4 py-5 border-t-[2px] border-bhav-cool-gray-50">
            <textarea class="max-w-md bg-bhav-cool-gray-50 text-bhav-blue-500 text-sm w-full p-3 rounded-lg focus:border-bhav-blue-400 min-h-32" id="editing.description" v-model="editing.description" required maxlength="1000"></textarea>
        </div>

        <label for="editing.button_text" class="font-normal text-bhav-blue-300 py-5 border-t-[2px] border-bhav-cool-gray-50">
            Button Text <span class="text-oakwood-red cursor-help" title="This field is required">*</span>
        </label>
        <div class="text-oakwood-gray-900 lg:col-span-4 py-5 border-t-[2px] border-bhav-cool-gray-50">
            <TextInput id="editing.button_text" v-model="editing.button_text" placeholder="Button Text" required maxlength="255" class="max-w-80" />
        </div>

        <label for="editing.open_in_new_tab" class="font-normal text-bhav-blue-300 py-5 border-t-[2px] border-bhav-cool-gray-50">
            Open in New Tab
        </label>
        <div class="text-oakwood-gray-900 lg:col-span-4 py-5 border-t-[2px] border-bhav-cool-gray-50">
            <ToggleButton label="Open in New Tab" :checked="editing.open_in_new_tab" @changed="editing.open_in_new_tab = $event" />
        </div>

        <label for="editing.disclaimer" class="font-normal text-bhav-blue-300 py-5 border-t-[2px] border-bhav-cool-gray-50">
            Disclaimer
        </label>
        <div class="text-oakwood-gray-900 lg:col-span-4 py-5 border-t-[2px] border-bhav-cool-gray-50">
            <textarea class="max-w-md bg-bhav-cool-gray-50 text-bhav-blue-500 text-sm w-full p-3 rounded-lg focus:border-bhav-blue-400 min-h-32" id="editing.disclaimer" v-model="editing.disclaimer" required maxlength="1000"></textarea>
        </div>

        <label for="editing.start_at" class="font-normal text-bhav-blue-300 py-5 border-t-[2px] border-bhav-cool-gray-50">
            Start Date
        </label>
        <div class="text-oakwood-gray-900 lg:col-span-4 py-5 border-t-[2px] border-bhav-cool-gray-50">
            <TextInput id="editing.start_at" type="datetime-local" v-model="editing.start_at" class="max-w-80" />
        </div>

        <label for="editing.end_at" class="font-normal text-bhav-blue-300 py-5 border-t-[2px] border-bhav-cool-gray-50">
            End Date
        </label>
        <div class="text-oakwood-gray-900 lg:col-span-4 py-5 border-t-[2px] border-bhav-cool-gray-50">
            <TextInput id="editing.end_at" type="datetime-local" v-model="editing.end_at" class="max-w-80" />
        </div>

        <label for="editing.url" class="font-normal text-bhav-blue-300 py-5 border-t-[2px] border-bhav-cool-gray-50">
            URL
        </label>
        <div class="text-oakwood-gray-900 lg:col-span-4 py-5 border-t-[2px] border-bhav-cool-gray-50">
            <URLField id="editing.url" v-model="editing.url" class="max-w-80" />
        </div>

        <label for="editing.file" class="font-normal text-bhav-blue-300 py-5 border-t-[2px] border-bhav-cool-gray-50">
            File
        </label>
        <div class="text-oakwood-gray-900 lg:col-span-4 py-5 border-t-[2px] border-bhav-cool-gray-50">
            <UploadFiles v-model="asset_file" :multiple="false" format="file" :accept="['application/pdf']" />
        </div>

        <label class="font-normal text-bhav-blue-300 py-5 border-t-[2px] border-bhav-cool-gray-50">
            Small Image
        </label>
        <div class="text-oakwood-gray-900 lg:col-span-4 py-5 border-t-[2px] border-bhav-cool-gray-50">
            <UploadFiles v-model="small_image" :multiple="false" />
            <TextInput v-model="editing.meta.small_image_alt" placeholder="Alt text" class="max-w-80 mt-2" />
        </div>

        <label class="font-normal text-bhav-blue-300 py-5 border-t-[2px] border-bhav-cool-gray-50">
            Large Image <span class="text-oakwood-red cursor-help" title="This field is required">*</span>
        </label>
        <div class="text-oakwood-gray-900 lg:col-span-4 py-5 border-t-[2px] border-bhav-cool-gray-50">
            <UploadFiles v-model="large_image" :multiple="false" />
            <TextInput v-model="editing.meta.large_image_alt" placeholder="Alt text" class="max-w-80 mt-2" />
        </div>

        <label for="editing.meta.show_image_inline" class="font-normal text-bhav-blue-300 py-5 border-t-[2px] border-bhav-cool-gray-50">
            Show image inline on page
        </label>
        <div class="text-oakwood-gray-900 lg:col-span-4 py-5 border-t-[2px] border-bhav-cool-gray-50">
            <ToggleButton label="Show image inline on page" :checked="editing.meta.show_image_inline" @changed="editing.meta.show_image_inline = $event" />
        </div>
    </form>
    </div>
</template>
